//https://www.primefaces.org/primeng/showcase/#/toast

import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, inject } from '@angular/core';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { NotificationsService } from '../notifications/notifications.service';
import {
	Notification,
	NotificationLink,
	NotificationLinkNavigation,
	ResolverNotificationLink,
} from '../notifications/notifications.model';
import { Subscription } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { filter, tap } from 'rxjs/operators';
import { ToastModule } from 'primeng/toast';

export type ToastPosition =
	'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | 'center';

@Component({
	selector: 'app-toast',
	templateUrl: './toast.component.html',
	styleUrls: ['./toast.component.scss'],
	providers: [MessageService],
	imports: [ToastModule, PrimeTemplate],
})
export class ToastComponent implements OnInit, OnDestroy {
	private notificationsService = inject(NotificationsService);
	private messageService = inject(MessageService);
	private router = inject(Router);

	@Input() position: ToastPosition = 'top-center';
	@Input() key: string = 'app-notification';

	/*
    actionButtonText is ONLY available for toasts imported locally 
    - NOT the global notification service toasts
  */
	@Input() actionButtonText?: string;

	@Output() toastClicked = new EventEmitter<Notification>();

	addSubscription$: Subscription;
	clearSubscription$: Subscription;
	innerHtml: boolean = false;
	routerSubscription$: Subscription;

	constructor() {
		this.routerSubscription$ = this.router.events
			.pipe(
				filter((event) => event instanceof NavigationStart && event.url != this.router.url),
				tap(() => this.clearNotifications())
			)
			.subscribe();
	}

	onClick(notification: Notification): void {
		this.toastClicked.emit(notification);
	}

	onLinkClick(link: NotificationLink): void {
		// if using a resolver link then synchronously execute
		// the resolve callback first (e.g. create a dataset)
		// then follow the link navigation
		if (link instanceof ResolverNotificationLink) {
			link.resolveOnClick().subscribe((navigation) => this.navigateToLink(navigation));
			return;
		}

		// otherwise, just follow the link
		this.navigateToLink(link);
	}

	private navigateToLink(navigation: NotificationLinkNavigation): void {
		const urlTree = this.router.createUrlTree(
			navigation.commands,
			navigation.queryParams ? { queryParams: navigation.queryParams } : {}
		);
		window.open(this.router.serializeUrl(urlTree), '_blank');
	}

	ngOnInit(): void {
		this.subscribeToNotifications();
		this.subscribeToClearNotifications();
	}

	private subscribeToNotifications(): void {
		this.addSubscription$ = this.notificationsService.notificationChange$.subscribe((notification) => {
			this.messageService.add(notification);
		});
	}

	private subscribeToClearNotifications(): void {
		this.clearSubscription$ = this.notificationsService.notificationClear$.subscribe(() =>
			this.clearNotifications()
		);
	}

	private clearNotifications(): void {
		this.messageService.clear();
	}

	ngOnDestroy(): void {
		this.addSubscription$.unsubscribe();
		this.clearSubscription$.unsubscribe();
		this.routerSubscription$.unsubscribe();
	}
}

